import { useId, useState } from 'react';
import { Form, InputGroup, Button } from 'react-bootstrap';

/**
 * Free-text list editor used for complaints, diagnosis, allergies and advice.
 * Enter adds the value, the x on a chip removes it.
 *
 * The suggestions underneath are reachable from the keyboard: arrow down/up
 * walks them, Enter takes the highlighted one, Escape drops back to what was
 * typed. Focus never leaves the box, so the doctor can keep going without
 * reaching for the mouse.
 */
export default function TagInput({
  value = [],
  onChange,
  placeholder = 'Type and press Enter',
  suggestions = [],
  disabled,
}) {
  const [draft, setDraft] = useState('');
  const [highlight, setHighlight] = useState(-1);
  const listId = useId();

  function add(text) {
    const clean = String(text).trim();
    setHighlight(-1);
    if (!clean) return;
    if (value.some((v) => v.toLowerCase() === clean.toLowerCase())) {
      setDraft('');
      return;
    }
    onChange([...value, clean]);
    setDraft('');
  }

  function remove(index) {
    onChange(value.filter((_, i) => i !== index));
  }

  const matches = draft.trim()
    ? suggestions
        .filter(
          (s) =>
            s.toLowerCase().includes(draft.trim().toLowerCase()) &&
            !value.some((v) => v.toLowerCase() === s.toLowerCase()),
        )
        .slice(0, 6)
    : [];

  // The list shrinks as you type, so never point past the end of it.
  const active = highlight < matches.length ? highlight : -1;

  function handleKeyDown(e) {
    if (e.key === 'ArrowDown' && matches.length) {
      e.preventDefault();
      setHighlight(active + 1 >= matches.length ? 0 : active + 1);
      return;
    }

    if (e.key === 'ArrowUp' && matches.length) {
      e.preventDefault();
      setHighlight(active <= 0 ? matches.length - 1 : active - 1);
      return;
    }

    if (e.key === 'Enter') {
      e.preventDefault();
      // Nothing highlighted means take what was actually typed.
      add(active >= 0 ? matches[active] : draft);
      return;
    }

    if (e.key === 'Escape' && active >= 0) {
      e.preventDefault();
      setHighlight(-1);
      return;
    }

    if (e.key === 'Backspace' && !draft && value.length) remove(value.length - 1);
  }

  return (
    <div>
      <InputGroup size="sm">
        <Form.Control
          value={draft}
          disabled={disabled}
          placeholder={placeholder}
          autoComplete="off"
          role="combobox"
          aria-expanded={matches.length > 0}
          aria-controls={listId}
          aria-activedescendant={active >= 0 ? `${listId}-${active}` : undefined}
          onChange={(e) => {
            setDraft(e.target.value);
            setHighlight(-1);
          }}
          onKeyDown={handleKeyDown}
        />
        <Button variant="outline-secondary" onClick={() => add(draft)} disabled={disabled || !draft.trim()}>
          <i className="bi bi-plus-lg" />
        </Button>
      </InputGroup>

      {matches.length > 0 && (
        <div className="d-flex flex-wrap gap-1 mt-1" id={listId} role="listbox">
          {matches.map((s, i) => (
            <Button
              key={s}
              id={`${listId}-${i}`}
              role="option"
              aria-selected={i === active}
              size="sm"
              variant={i === active ? 'secondary' : 'outline-secondary'}
              className="tag-suggest"
              // Keep the caret in the box when one is clicked with the mouse.
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => add(s)}
            >
              {s}
            </Button>
          ))}
        </div>
      )}

      {value.length > 0 && (
        <div className="d-flex flex-wrap gap-2 mt-2">
          {value.map((item, i) => (
            <span className="tag-chip" key={`${item}-${i}`}>
              {item}
              {!disabled && (
                <button type="button" className="btn-x" onClick={() => remove(i)} aria-label={`Remove ${item}`}>
                  <i className="bi bi-x-lg" style={{ fontSize: '0.7rem' }} />
                </button>
              )}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
